import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';

/**
 * RecentReflections — a unified, read-only timeline that interleaves the
 * user's past session entries and chat exchanges. It exists to give a quiet
 * sense of "I came back to myself, more than once" — never as an engagement
 * hook, never with notifications, never with metrics. The data is pulled
 * from /api/sessions and /api/chat/history (both already device-id gated)
 * and merged client-side by timestamp.
 */
export default function RecentReflections() {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Both endpoints can fail independently — a missing/expired chat
        // history shouldn't hide the user's session timeline and vice versa.
        const [sessionsRes, chatRes] = await Promise.allSettled([
          api.get('/sessions'),
          api.get('/chat/history'),
        ]);
        if (cancelled) return;

        const merged = [];

        if (sessionsRes.status === 'fulfilled') {
          for (const s of sessionsRes.value.data || []) {
            // Only show completed sessions — incomplete ones risk surfacing
            // half-formed thoughts the user may not want to see again.
            if (!s.completed) continue;
            merged.push({
              id: `s-${s.id}`,
              kind: 'session',
              timestamp: s.created_at,
              session_id: s.id,
            });
          }
        }

        if (chatRes.status === 'fulfilled') {
          // Only the user's own messages — assistant replies on their own
          // would feel ghostly and out of context.
          for (const m of chatRes.value.data?.messages || []) {
            if (m.role !== 'user') continue;
            merged.push({
              id: `c-${m.created_at}-${m.content.slice(0, 12)}`,
              kind: 'chat',
              timestamp: m.created_at,
              content: m.content,
            });
          }
        }

        merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setItems(merged.slice(0, 20));  // small window — this is a glance, not an archive
      } catch (e) {
        console.error('RecentReflections load failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      const locale = lang === 'fi' ? 'fi-FI' : undefined;
      return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  return (
    <section data-testid="settings-reflections">
      <div className="ctl-eyebrow mb-3">{t('settings.reflectionsTitle')}</div>
      <p className="ctl-helper text-sm mb-6">{t('settings.reflectionsHelp')}</p>

      {loading && (
        <p className="ctl-helper text-sm" data-testid="reflections-loading">…</p>
      )}

      {!loading && items.length === 0 && (
        <p className="ctl-helper text-sm" data-testid="reflections-empty">
          {t('settings.reflectionsEmpty')}
        </p>
      )}

      {!loading && items.length > 0 && (
        <ul className="space-y-4 border-l border-[#E3E1DB] pl-6" data-testid="reflections-list">
          {items.map((item) => (
            <li key={item.id} className="relative" data-testid={`reflection-${item.kind}-${item.id}`}>
              <span
                className="absolute -left-[27px] top-2 w-2 h-2 rounded-full bg-[#968369]"
                aria-hidden="true"
              />
              <div className="ctl-eyebrow text-xs mb-1" style={{ opacity: 0.7 }}>
                {item.kind === 'session'
                  ? t('settings.reflectionSession')
                  : t('settings.reflectionChat')}
                {' · '}
                {formatDate(item.timestamp)}
              </div>
              {item.kind === 'chat' && (
                <p className="text-[#1C1B1A] text-sm leading-relaxed line-clamp-3">
                  {item.content}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
